import React, { useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Chip
} from '@mui/material';
import { OpenInFull } from '@mui/icons-material';
import ReactApexChart from 'react-apexcharts';
import { styled } from '@mui/material/styles';
import StatCards from './StatCards';
import ExportButton from './ExportButton';
import BubbleMaps from './BuubleMapChart';
import { formatCurrency, formatNumber } from './ExportUtils';

// ─── Styled ───────────────────────────────────────────────────────────────────

const ChartContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1),
  height: '270px',
  display: 'flex',
  flexDirection: 'column',
  minHeight: '270px'
}));

const StyledToggle = styled(ToggleButton)(() => ({
  padding: '3px 12px',
  fontSize: '0.7rem',
  fontWeight: 600,
  textTransform: 'none',
  border: '1px solid #d0d7de',
  color: '#666',
  '&.Mui-selected': {
    backgroundColor: '#1565C0 !important',
    color: '#fff !important',
    borderColor: '#1565C0 !important'
  },
  '&:hover': { backgroundColor: '#f0f4ff' }
}));

// ─── Small badge shown in every card title when YOY or YTD is active ─────────

const PeriodBadge = ({ viewMode }) => {
  if (viewMode === 'normal') return null;

  const isYoy = viewMode === 'yoy';
  const label = isYoy ? 'YOY' : 'YTD';
  const color = isYoy ? '#1565C0' : '#2e7d32';

  return (
    <Chip
      label={label}
      size='small'
      sx={{
        height: 16,
        fontSize: '0.6rem',
        fontWeight: 700,
        ml: 0.75,
        backgroundColor: color,
        color: '#fff',
        borderRadius: '4px',
        '& .MuiChip-label': { px: 0.75 }
      }}
    />
  );
};

// ─── Component ────────────────────────────────────────────────────────────────

const FreightSpendTab = ({
  shipments,
  statCards,
  monthlyData,
  monthlyShipmentTypeData,
  carrierNames,
  carrierSpend,
  carrierMap,
  avgCostPerShipmentData,
  costPerPoundData,
  freightByWeightData,
  freightByMilesData,
  shipTypePieSeries,
  shipTypePieOptionsWithClick,
  setExpandedChart,
  handleMonthFilter,
  handleCarrierFilter,
  handleWeightFilter,
  handleMileFilter,
  weightRangeNames,
  mileRangeNames,
  expandedChart
}) => {
  // ── Single new piece of state ─────────────────────────────────────────────
  const [viewMode, setViewMode] = useState('normal'); // 'normal' | 'ytd' | 'yoy'

  // Export data (unchanged)
  const getFreightSpendExportData = () => {
    return shipments.map(shipment => ({
      'Shipment ID': shipment.shipmentId || shipment.id,
      Customer: shipment.destinationCompany,
      Origin: `${shipment.originCity}, ${shipment.originState}`,
      Destination: `${shipment.destinationCity}, ${shipment.destinationState}`,
      Carrier: shipment.carrierName,
      'Shipment Type': shipment.shipmentType,
      'Shipment Mode': shipment.shipmentTypeRule,
      'Pickup Date': shipment.actualPickupDate,
      'Delivery Date': shipment.actualDeliveryDate,
      'Freight Spend': shipment.quoteNetCharge,
      'Weight (lbs)': shipment.totalGrossWeight,
      Miles: shipment.shipmentMiles
        ? (shipment.shipmentMiles / 1609.344).toFixed(0)
        : 0,
      Status: shipment.shipmentStatus
    }));
  };

  return (
    <Box>
      {/* ── Toggle — top right, above all cards ─────────────────────────── */}

      {/* ── All 11 cards — identical to original, just PeriodBadge added ── */}
      <Grid container spacing={0.5} sx={{ mb: 1 }}>
        <Grid item xs={12} md={9}>
          <Grid container spacing={0.5}>
            {/* Card 1 — Stat Cards */}
            <Grid item xs={12} md={4}>
              <ChartContainer elevation={1}>
                {/* Badge row above StatCards */}
                {viewMode !== 'normal' && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <Typography
                      variant='caption'
                      sx={{
                        fontSize: '0.68rem',
                        color: '#777',
                        fontWeight: 500
                      }}
                    >
                      Showing:
                    </Typography>
                    <PeriodBadge viewMode={viewMode} />
                  </Box>
                )}
                <StatCards cards={statCards} />
              </ChartContainer>
            </Grid>

            {/* Card 2 — Monthly Freight Spend */}
            <Grid item xs={12} md={4}>
              <ChartContainer elevation={1}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant='h6' fontWeight='bold' fontSize='1rem'>
                      Monthly Freight Spend
                    </Typography>
                    <PeriodBadge viewMode={viewMode} />
                  </Box>
                  <IconButton
                    size='small'
                    onClick={() => setExpandedChart('monthlyFreight')}
                  >
                    <OpenInFull fontSize='small' />
                  </IconButton>
                </Box>
                <ReactApexChart
                  options={{
                    chart: {
                      type: 'bar',
                      height: 220,
                      toolbar: { show: false },
                      events: {
                        dataPointSelection: (event, chartContext, config) => {
                          const selectedMonth =
                            monthlyData[config.dataPointIndex]?.month;
                          if (selectedMonth) handleMonthFilter(selectedMonth);
                        }
                      }
                    },
                    plotOptions: {
                      bar: {
                        borderRadius: 2,
                        columnWidth: '50%',
                        dataLabels: { position: 'top' }
                      }
                    },
                    dataLabels: {
                      enabled: true,
                      formatter: val =>
                        val >= 1000
                          ? '$' + (val / 1000).toFixed(0) + 'K'
                          : '$' + Math.round(val),
                      offsetY: -20,
                      style: {
                        fontSize: '9px',
                        colors: ['#304758'],
                        fontWeight: 'bold'
                      }
                    },
                    xaxis: {
                      categories: monthlyData.map(item => item.month),
                      labels: {
                        style: { fontSize: '10px' },
                        rotate: -45,
                        trim: false
                      }
                    },
                    yaxis: {
                      tickAmount: 6,
                      labels: {
                        formatter: val =>
                          val >= 1000
                            ? '$' + (val / 1000).toFixed(0) + 'K'
                            : '$' + Math.round(val),
                        style: { fontSize: '10px' }
                      }
                    },
                    grid: { padding: { left: 5, right: 5 } },
                    colors: ['#008FFB'],
                    tooltip: {
                      y: {
                        formatter: val =>
                          'Total Freight: $' + val.toLocaleString()
                      }
                    }
                  }}
                  series={[
                    {
                      name: 'Freight Spend',
                      data: monthlyData.map(item => Math.round(item.freight))
                    }
                  ]}
                  type='bar'
                  height={220}
                />
              </ChartContainer>
            </Grid>

            {/* Card 3 — Monthly Freight by Shipment Type */}
            <Grid item xs={12} md={4}>
              <ChartContainer elevation={1}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant='h6' fontWeight='bold' fontSize='1rem'>
                      Monthly Freight by Shipment Type
                    </Typography>
                    <PeriodBadge viewMode={viewMode} />
                  </Box>
                  <IconButton
                    size='small'
                    onClick={() => setExpandedChart('shipmentTypeMonthly')}
                  >
                    <OpenInFull fontSize='small' />
                  </IconButton>
                </Box>
                <ReactApexChart
                  options={{
                    chart: {
                      type: 'bar',
                      height: 220,
                      toolbar: { show: false },
                      events: {
                        dataPointSelection: (event, chartContext, config) => {
                          const selectedMonth =
                            monthlyShipmentTypeData.categories[
                              config.dataPointIndex
                            ];
                          if (selectedMonth) handleMonthFilter(selectedMonth);
                        }
                      }
                    },
                    plotOptions: {
                      bar: {
                        borderRadius: 2,
                        columnWidth: '50%',
                        dataLabels: { position: 'top' }
                      }
                    },
                    dataLabels: {
                      enabled: true,
                      formatter: val =>
                        val >= 1000
                          ? '$' + (val / 1000).toFixed(0) + 'K'
                          : val > 0
                            ? '$' + Math.round(val)
                            : '',
                      offsetY: -20,
                      style: {
                        fontSize: '9px',
                        colors: ['#304758'],
                        fontWeight: 'bold'
                      }
                    },
                    xaxis: {
                      categories: monthlyShipmentTypeData.categories,
                      labels: {
                        style: { fontSize: '10px' },
                        rotate: -45,
                        trim: false
                      }
                    },
                    yaxis: {
                      labels: {
                        formatter: val =>
                          val >= 1000
                            ? '$' + (val / 1000).toFixed(0) + 'K'
                            : '$' + Math.round(val),
                        style: { fontSize: '10px' }
                      }
                    },
                    grid: { padding: { left: 5, right: 5 } },
                    colors: ['#008FFB', '#00E396', '#FF4560'],
                    legend: { position: 'top', fontSize: '12px' },
                    tooltip: {
                      y: {
                        formatter: (val, { seriesIndex }) => {
                          const type = ['LTL', 'Parcel', 'TL'][seriesIndex];
                          return type + ': $' + val.toLocaleString();
                        }
                      }
                    }
                  }}
                  series={monthlyShipmentTypeData.series}
                  type='bar'
                  height={220}
                />
              </ChartContainer>
            </Grid>

            {/* Card 4 — Freight Spent By Carrier */}
            <Grid item xs={12} md={4}>
              <ChartContainer elevation={1}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant='h6' fontWeight='bold' fontSize='1rem'>
                      Freight Spent By Carrier
                    </Typography>
                    <PeriodBadge viewMode={viewMode} />
                  </Box>
                  <IconButton
                    size='small'
                    onClick={() => setExpandedChart('carrierFreight')}
                  >
                    <OpenInFull fontSize='small' />
                  </IconButton>
                </Box>
                <Box sx={{ overflowX: 'auto', overflowY: 'visible' }}>
                  <ReactApexChart
                    options={{
                      chart: {
                        type: 'bar',
                        height: 280,
                        toolbar: { show: false },
                        events: {
                          dataPointSelection: (event, chartContext, config) => {
                            const selectedCarrier =
                              carrierNames[config.dataPointIndex];
                            if (selectedCarrier)
                              handleCarrierFilter(selectedCarrier);
                          }
                        }
                      },
                      legend: { show: false },
                      plotOptions: {
                        bar: {
                          distributed: true,
                          horizontal: true,
                          barHeight: '95%',
                          dataLabels: {
                            position: 'right',
                            hideOverflowingLabels: false
                          }
                        }
                      },
                      dataLabels: {
                        enabled: true,
                        formatter: val => {
                          const millions = val / 1000000;
                          return (
                            '\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0$' +
                            millions.toFixed(1) +
                            'M'
                          );
                        },
                        style: {
                          fontSize: '11px',
                          fontWeight: 'bold',
                          colors: ['#304758'],
                          textShadow: '1px 1px 2px white'
                        },
                        background: { enabled: false },
                        offsetX: 10
                      },
                      xaxis: {
                        min: Math.max(...carrierSpend) * 0.0,
                        max: Math.max(...carrierSpend) * 1.1,
                        labels: {
                          formatter: val =>
                            '$' + (val / 1000000).toFixed(1) + 'M',
                          style: { fontSize: '11px' }
                        }
                      },
                      yaxis: {
                        categories: carrierNames,
                        labels: {
                          show: true,
                          minWidth: 60,
                          maxWidth: 60,
                          style: { fontSize: '11px' }
                        }
                      },
                      grid: { padding: { left: 10, right: 15 } },
                      colors: [
                        '#008FFB',
                        '#00E396',
                        '#FEB019',
                        '#FF4560',
                        '#775DD0',
                        '#3F51B5',
                        '#546E7A',
                        '#D4526E',
                        '#8D5B4C',
                        '#F86624',
                        '#663399',
                        '#E91E63',
                        '#9C27B0',
                        '#673AB7',
                        '#3F51B5'
                      ],
                      tooltip: {
                        y: {
                          formatter: (val, { dataPointIndex, w }) => {
                            const carrierName = w.config.labels[dataPointIndex];
                            const shipmentCount =
                              carrierMap[carrierName]?.shipmentCount || 0;
                            return `${carrierName}: $${val.toLocaleString()} (${shipmentCount} shipments)`;
                          }
                        }
                      },
                      labels: carrierNames
                    }}
                    series={[{ name: 'Freight Spend', data: carrierSpend }]}
                    type='bar'
                    height={280}
                  />
                </Box>
              </ChartContainer>
            </Grid>

            {/* Card 5 — Average Cost per Shipment */}
            <Grid item xs={12} md={4}>
              <ChartContainer elevation={1}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant='h6' fontWeight='bold' fontSize='1rem'>
                      Average Cost per Shipment
                    </Typography>
                    <PeriodBadge viewMode={viewMode} />
                  </Box>
                  <IconButton
                    size='small'
                    onClick={() => setExpandedChart('avgCostPerShipment')}
                  >
                    <OpenInFull fontSize='small' />
                  </IconButton>
                </Box>
                <ReactApexChart
                  options={{
                    chart: {
                      type: 'bar',
                      height: 220,
                      toolbar: { show: false },
                      events: {
                        dataPointSelection: (event, chartContext, config) => {
                          const selectedMonth =
                            avgCostPerShipmentData.categories[
                              config.dataPointIndex
                            ];
                          if (selectedMonth) handleMonthFilter(selectedMonth);
                        }
                      }
                    },
                    plotOptions: {
                      bar: {
                        borderRadius: 2,
                        columnWidth: '50%',
                        dataLabels: { position: 'top' }
                      }
                    },
                    dataLabels: {
                      enabled: true,
                      formatter: val => '$' + val.toFixed(0),
                      offsetY: -20,
                      style: {
                        fontSize: '9px',
                        colors: ['#304758'],
                        fontWeight: 'bold'
                      }
                    },
                    xaxis: {
                      categories: avgCostPerShipmentData.categories,
                      labels: {
                        style: { fontSize: '10px' },
                        rotate: -45,
                        trim: false
                      }
                    },
                    yaxis: {
                      labels: {
                        formatter: val => '$' + (val / 1000).toFixed(1) + 'K',
                        style: { fontSize: '10px' }
                      }
                    },
                    grid: { padding: { left: 5, right: 5 } },
                    colors: ['#775DD0'],
                    tooltip: {
                      y: {
                        formatter: val =>
                          'Avg Cost: $' + Math.round(val).toLocaleString()
                      }
                    }
                  }}
                  series={[
                    {
                      name: 'Avg Cost per Shipment',
                      data: avgCostPerShipmentData.data
                    }
                  ]}
                  type='bar'
                  height={220}
                />
              </ChartContainer>
            </Grid>

            {/* Card 6 — Cost per Pound */}
            <Grid item xs={12} md={4}>
              <ChartContainer elevation={1}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant='h6' fontWeight='bold' fontSize='1rem'>
                      Cost per Pound
                    </Typography>
                    <PeriodBadge viewMode={viewMode} />
                  </Box>
                  <IconButton
                    size='small'
                    onClick={() => setExpandedChart('costPerPound')}
                  >
                    <OpenInFull fontSize='small' />
                  </IconButton>
                </Box>
                <ReactApexChart
                  options={{
                    chart: {
                      type: 'bar',
                      height: 220,
                      toolbar: { show: false },
                      events: {
                        dataPointSelection: (event, chartContext, config) => {
                          const selectedMonth =
                            costPerPoundData.categories[config.dataPointIndex];
                          if (selectedMonth) handleMonthFilter(selectedMonth);
                        }
                      }
                    },
                    plotOptions: {
                      bar: {
                        borderRadius: 2,
                        columnWidth: '50%',
                        dataLabels: { position: 'top' }
                      }
                    },
                    dataLabels: {
                      enabled: true,
                      formatter: val => '$' + val.toFixed(3),
                      offsetY: -20,
                      style: {
                        fontSize: '9px',
                        colors: ['#304758'],
                        fontWeight: 'bold'
                      }
                    },
                    xaxis: {
                      categories: costPerPoundData.categories,
                      labels: {
                        style: { fontSize: '10px' },
                        rotate: -45,
                        trim: false
                      }
                    },
                    yaxis: {
                      labels: {
                        formatter: val => '$' + val.toFixed(1),
                        style: { fontSize: '10px' }
                      }
                    },
                    grid: { padding: { left: 5, right: 5 } },
                    colors: ['#FF4560'],
                    tooltip: {
                      y: {
                        formatter: val => 'Cost per Pound: $' + val.toFixed(2)
                      }
                    }
                  }}
                  series={[
                    { name: 'Cost per Pound', data: costPerPoundData.data }
                  ]}
                  type='bar'
                  height={220}
                />
              </ChartContainer>
            </Grid>

            {/* Card 7 — Freight Spend by Weight */}
            <Grid item xs={12} md={4}>
              <ChartContainer elevation={1}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant='h6' fontWeight='bold' fontSize='1rem'>
                      Freight Spend by Weight (lbs)
                    </Typography>
                    <PeriodBadge viewMode={viewMode} />
                  </Box>
                  <IconButton
                    size='small'
                    onClick={() => setExpandedChart('freightByWeight')}
                  >
                    <OpenInFull fontSize='small' />
                  </IconButton>
                </Box>
                <Box sx={{ overflowX: 'auto', overflowY: 'visible' }}>
                  <ReactApexChart
                    options={{
                      chart: {
                        type: 'bar',
                        height: 250,
                        toolbar: { show: false },
                        legend: { show: false },
                        events: {
                          dataPointSelection: (event, chartContext, config) => {
                            const selectedWeightRange =
                              weightRangeNames[config.dataPointIndex];
                            if (selectedWeightRange)
                              handleWeightFilter(selectedWeightRange);
                          }
                        }
                      },
                      plotOptions: {
                        bar: {
                          distributed: true,
                          horizontal: true,
                          barHeight: '60%',
                          dataLabels: {
                            position: 'right',
                            hideOverflowingLabels: false
                          }
                        }
                      },
                      legend: { show: false },
                      dataLabels: {
                        enabled: true,
                        formatter: val => {
                          const pad =
                            '\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0';
                          return val >= 1000
                            ? pad + '$' + (val / 1000).toFixed(0) + 'K'
                            : pad + '$' + Math.round(val);
                        },
                        style: {
                          fontSize: '11px',
                          fontWeight: 'bold',
                          colors: ['#304758'],
                          textShadow: '1px 1px 2px white'
                        },
                        background: { enabled: false },
                        offsetX: 2
                      },
                      xaxis: {
                        labels: {
                          formatter: val =>
                            val >= 1000
                              ? '$' + (val / 1000).toFixed(0) + 'K'
                              : '$' + Math.round(val),
                          style: { fontSize: '11px' }
                        },
                        title: {
                          text: 'Freight Spend',
                          style: { fontSize: '12px', fontWeight: 'bold' }
                        }
                      },
                      yaxis: {
                        categories: [
                          '<500',
                          '500-999',
                          '1,000-1,999',
                          '2,000-4,999',
                          '5,000-9,999',
                          '10,000-19,999',
                          '20,000-40,000',
                          '>40,000'
                        ],
                        labels: {
                          show: true,
                          minWidth: 100,
                          maxWidth: 120,
                          style: { fontSize: '11px', fontWeight: 'bold' }
                        }
                      },
                      grid: { padding: { left: -5, right: 15 } },
                      colors: [
                        '#008FFB',
                        '#00E396',
                        '#FEB019',
                        '#FF4560',
                        '#775DD0',
                        '#3F51B5',
                        '#546E7A'
                      ],
                      tooltip: {
                        y: {
                          formatter: (val, { dataPointIndex }) => {
                            const weightRanges = [
                              '<500',
                              '500-999',
                              '1,000-1,999',
                              '2,000-4,999',
                              '5,000-9,999',
                              '10,000-19,999',
                              '20,000-40,000',
                              '>40,000'
                            ];
                            return `${weightRanges[dataPointIndex]}: $${val.toLocaleString()}`;
                          }
                        }
                      },
                      labels: [
                        '<500',
                        '500-999',
                        '1,000-1,999',
                        '2,000-4,999',
                        '5,000-9,999',
                        '10,000-19,999',
                        '20,000-40,000',
                        '>40,000'
                      ]
                    }}
                    series={[
                      { name: 'Freight Spend', data: freightByWeightData }
                    ]}
                    type='bar'
                    height={250}
                  />
                </Box>
              </ChartContainer>
            </Grid>

            {/* Card 8 — Freight Spend by Miles */}
            <Grid item xs={12} md={4}>
              <ChartContainer elevation={1}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant='h6' fontWeight='bold' fontSize='1rem'>
                      Freight Spend by Miles
                    </Typography>
                    <PeriodBadge viewMode={viewMode} />
                  </Box>
                  <IconButton
                    size='small'
                    onClick={() => setExpandedChart('freightByMiles')}
                  >
                    <OpenInFull fontSize='small' />
                  </IconButton>
                </Box>
                <Box sx={{ overflowX: 'auto', overflowY: 'visible' }}>
                  <ReactApexChart
                    options={{
                      chart: {
                        type: 'bar',
                        height: 280,
                        toolbar: { show: false },
                        events: {
                          dataPointSelection: (event, chartContext, config) => {
                            const selectedMileRange =
                              mileRangeNames[config.dataPointIndex];
                            if (selectedMileRange)
                              handleMileFilter(selectedMileRange);
                          }
                        }
                      },
                      plotOptions: {
                        bar: {
                          distributed: true,
                          horizontal: true,
                          barHeight: '60%',
                          dataLabels: {
                            position: 'right',
                            hideOverflowingLabels: false
                          }
                        }
                      },
                      legend: { show: false },
                      dataLabels: {
                        enabled: true,
                        formatter: val => {
                          const pad =
                            '\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0';
                          return val >= 1000
                            ? pad + '$' + (val / 1000).toFixed(0) + 'K'
                            : pad + '$' + Math.round(val);
                        },
                        style: {
                          fontSize: '11px',
                          fontWeight: 'bold',
                          colors: ['#304758'],
                          textShadow: '1px 1px 2px white'
                        },
                        background: { enabled: false },
                        offsetX: 20
                      },
                      xaxis: {
                        labels: {
                          formatter: val =>
                            val >= 1000
                              ? '$' + (val / 1000).toFixed(0) + 'K'
                              : '$' + Math.round(val),
                          style: { fontSize: '11px' }
                        },
                        title: {
                          text: 'Freight Spend',
                          style: { fontSize: '12px', fontWeight: 'bold' }
                        }
                      },
                      yaxis: {
                        categories: [
                          '0-49',
                          '50-99',
                          '100-249',
                          '250-499',
                          '500-749',
                          '750-999',
                          '1,000-1,499',
                          '1,500-1,999',
                          '2,000-2,499',
                          '2,500+'
                        ],
                        labels: {
                          show: true,
                          minWidth: 80,
                          maxWidth: 100,
                          style: { fontSize: '11px', fontWeight: 'bold' }
                        }
                      },
                      grid: { padding: { left: 5, right: 5 } },
                      colors: [
                        '#008FFB',
                        '#00E396',
                        '#FEB019',
                        '#FF4560',
                        '#775DD0',
                        '#3F51B5',
                        '#546E7A',
                        '#D4526E',
                        '#8D5B4C'
                      ],
                      tooltip: {
                        y: {
                          formatter: (val, { dataPointIndex }) => {
                            const mileRanges = [
                              '0-49 miles',
                              '50-99 miles',
                              '100-249 miles',
                              '250-499 miles',
                              '500-749 miles',
                              '750-999 miles',
                              '1,000-1,499 miles',
                              '1,500-1,999 miles',
                              '2,000-2,499 miles',
                              '2,500+ miles'
                            ];
                            return `${mileRanges[dataPointIndex]}: $${val.toLocaleString()}`;
                          }
                        }
                      },
                      labels: [
                        '0-49',
                        '50-99',
                        '100-249',
                        '250-499',
                        '500-749',
                        '750-999',
                        '1,000-1,499',
                        '1,500-1,999',
                        '2,000-2,499',
                        '2,500+'
                      ]
                    }}
                    series={[
                      { name: 'Freight Spend', data: freightByMilesData }
                    ]}
                    type='bar'
                    height={280}
                  />
                </Box>
              </ChartContainer>
            </Grid>

            {/* Card 9 — Freight Spend by Shipment Type */}
            <Grid item xs={12} md={4}>
              <ChartContainer elevation={1}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant='h6' fontWeight='bold' fontSize='1rem'>
                      Freight Spend by Shipment Type
                    </Typography>
                    <PeriodBadge viewMode={viewMode} />
                  </Box>
                  <IconButton
                    size='small'
                    onClick={() => setExpandedChart('shipmentTypePie')}
                  >
                    <OpenInFull fontSize='small' />
                  </IconButton>
                </Box>
                <ReactApexChart
                  options={shipTypePieOptionsWithClick}
                  series={shipTypePieSeries}
                  type='donut'
                  height={220}
                />
              </ChartContainer>
            </Grid>
          </Grid>
        </Grid>

        {/* Cards 10 & 11 — BubbleMaps (origin + destination are inside this component) */}
        <Grid item xs={12} md={3}>
          {/* Badge above the bubble maps */}
          {viewMode !== 'normal' && (
            <Box
              sx={{ display: 'flex', alignItems: 'center', mb: 0.5, px: 0.5 }}
            >
              <Typography
                variant='caption'
                sx={{ fontSize: '0.68rem', color: '#777', fontWeight: 500 }}
              >
                Map view:
              </Typography>
              <PeriodBadge viewMode={viewMode} />
            </Box>
          )}
          <BubbleMaps
            shipments={shipments}
            setExpandedChart={setExpandedChart}
            expandedChart={expandedChart}
            tabValue={0}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default FreightSpendTab;
